import React, { useState, useRef } from "react";
import { CSVLink } from "react-csv";
import { Modal, Button, Spinner } from "react-bootstrap";
import apiClient from "../../api/apiClient";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import TextField from '@mui/material/TextField';
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import TranslatedText from "../../hooks/TranslatedText";
import { formatDateTime } from "../../hooks/formatDateTime";
import { formatPrice } from "../../hooks/formatPrice";


const DataExport = ({ data }) => {
    const { t, i18n } = useTranslation();
    const [show, setShow] = useState(false);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [loading, setLoading] = useState(false);
    const [csvData, setCsvData] = useState([]);
    const csvLinkRef = useRef(null);

    const headers = [
        { label: t("order.OrderId"), key: "OrderID" },
        { label: t("order.Item"), key: "ProductName" },
        { label: t("order.BuyerName"), key: "BuyerName" },
        { label: t("order.Color"), key: "Color" },
        { label: t("order.Size"), key: "Size" },
        { label: t("order.Quantity"), key: "Quantity" },
        { label: t("order.TotalAmount"), key: "NetPayout" },
        { label: t("order.OrderStatus"), key: "Status" },
        { label: t("order.OrderDate"), key: "OrderDate" },
    ];

    const filename = `order_data_${new Date().toISOString().slice(0, 10)}.csv`;

    const buildCsvRows = (orders) => {
        return (
            orders?.flatMap((order) => {
                const items = order?.itemsdata || [];

                if (!items.length) {
                    return [
                        {
                            OrderID: order?.order_id ?? "—",
                            ProductName: "—",
                            BuyerName: order?.userdata?.user_name || order?.user_name || "—",
                            Color: "—",
                            Size: "—",
                            Quantity: order?.orders_quantity ?? 0,
                            NetPayout: formatPrice(order?.total_amount || 0, i18n),
                            Status: t(`orderStatus.${order?.order_status}`) || order?.order_status,
                            OrderDate: order?.order_created_at
                                ? formatDateTime(order.order_created_at)
                                : "—",
                        },
                    ];
                }

                return items.map((item) => {
                    let variant = null;

                    if (
                        order?.ordered_variant_id?.length &&
                        item?.variantsData?.length
                    ) {
                        variant = item.variantsData.find((v) =>
                            order.ordered_variant_id.includes(v.id)
                        );
                    }

                    if (!variant) {
                        variant = item?.variantsData?.[0];
                    }

                    return {
                        OrderID: order?.order_id ?? "—",
                        ProductName: item?.title ?? "—",
                        BuyerName:
                            order?.userdata?.user_name ||
                            order?.user_name ||
                            "—",

                        // CSV must contain text, not React components
                        Color:
                            variant?.color_name?.[i18n.language] ??
                            variant?.color_name ??
                            "—",

                        Size: variant?.size_name ?? "—",

                        Quantity:
                            items.length > 1
                                ? 1
                                : order?.orders_quantity ?? 1,

                        NetPayout: formatPrice(
                            order?.total_amount || 0,
                            i18n
                        ),

                        Status:
                            t(`orderStatus.${order?.order_status}`) ||
                            order?.order_status,

                        OrderDate: order?.order_created_at
                            ? formatDateTime(order.order_created_at)
                            : "—",
                    };
                });
            }) || []
        );
    };

    const handleOpen = () => setShow(true);
    const handleClose = () => {
        setShow(false);
        setStartDate(null);
        setEndDate(null);
        setCsvData([]);
        setLoading(false);
    };

    const handleFetchAndExport = async () => {
        if (!startDate || !endDate) return;
        setLoading(true);
        try {
            const params = {};
            params.startDate = dayjs(startDate).format("YYYY-MM-DD");
            params.endDate = dayjs(endDate).format("YYYY-MM-DD");
            const res = await apiClient.get("admin/order-list", { params });
            const orders = res?.data?.data || [];
            const rows = buildCsvRows(orders);
            setCsvData(rows);

            // wait for CSVLink to render then trigger download
            setTimeout(() => {
                if (csvLinkRef.current) {
                    csvLinkRef.current.link.click();
                }
                setLoading(false);
                handleClose();
            }, 100);
        } catch (error) {
            console.error(error);
            setLoading(false);
        }
    };

    return (
        <>
            <button className="orange-btn" onClick={handleOpen}>
                <i className="bi bi-download me-2"></i>
                {t("order.ExportOrderData")}
            </button>

            <Modal show={show} onHide={handleClose} centered className="export-modal">
                <Modal.Header closeButton>
                    <Modal.Title>{t("order.ExportOrderData")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="row g-3">
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <div className="col-12 col-md-6">
                                <DatePicker
                                    label={t("order.StartDate")}
                                    value={startDate}
                                    onChange={(newValue) => setStartDate(newValue)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            fullWidth
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: 10,
                                                    backgroundColor: '#fff',
                                                },
                                                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#e8671e',
                                                },
                                                '& .MuiInputLabel-root.Mui-focused': {
                                                    color: '#e8671e',
                                                },
                                                '& .MuiSvgIcon-root': {
                                                    color: '#e8671e',
                                                },
                                            }}
                                        />
                                    )}
                                />
                            </div>

                            <div className="col-12 col-md-6">
                                <DatePicker
                                    label={t("order.EndDate")}
                                    value={endDate}
                                    onChange={(newValue) => setEndDate(newValue)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            fullWidth
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: 10,
                                                    backgroundColor: '#fff',
                                                },
                                                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#e8671e',
                                                },
                                                '& .MuiInputLabel-root.Mui-focused': {
                                                    color: '#e8671e',
                                                },
                                                '& .MuiSvgIcon-root': {
                                                    color: '#e8671e',
                                                },
                                            }}
                                        />
                                    )}
                                />
                            </div>
                        </LocalizationProvider>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button className="ec-btn ec-btn-ghost" onClick={handleClose} disabled={loading}>{t("order.Cancel")}</button>
                    <button className="ec-btn ec-btn-primary" onClick={handleFetchAndExport} disabled={loading || !startDate || !endDate}>
                        {loading ? <><Spinner animation="border" size="sm" />&nbsp;{t("order.Exporting")}</> : t("order.Export")}
                    </button>
                </Modal.Footer>
            </Modal>

            <CSVLink
                data={csvData}
                headers={headers}
                filename={filename}
                className="d-none"
                ref={csvLinkRef}
            />
        </>
    );
};

export default DataExport;

